// Пагинация списка партий («Показать ещё» по 20). Поверх публичных API, парсеры —
// из ядра расширения (@ext, чистые). Курсоры на источник:
//   lichess  — until по timestamp самой старой уже загруженной партии;
//   chess.com — предыдущие месячные архивы (список месяцев, новые → старые).
// auto = оба источника параллельно, слияние + dedup по site:id. НЕ трогает
// extension/: сеть тут своя, из ядра берём только чистые парсеры.
import {
  type CoachGame,
  isValidNick,
  normalizeNick,
  parseLichessNdjson,
  normalizeChessComArchiveGames,
} from '@ext/core/game-sources';
import type { GameSource } from './games';

const PAGE = 20;

async function getJson(url: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
  const res = await fetch(url, { signal });
  if (res.status === 404) throw new Error('nick_not_found');
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

async function getText(url: string, headers: Record<string, string>, signal?: AbortSignal): Promise<string> {
  const res = await fetch(url, { signal, headers });
  if (res.status === 404) throw new Error('nick_not_found');
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.text();
}

/** Самая информативная ошибка из набора (сеть важнее «не найден»). */
function pickError(reasons: unknown[]): Error {
  const msgs = reasons.map((r) => (r instanceof Error ? r : new Error(String(r))));
  return msgs.find((e) => e.message !== 'nick_not_found') || msgs[0] || new Error('nick_not_found');
}

interface SourcePager {
  done: boolean;
  next(signal?: AbortSignal): Promise<CoachGame[]>;
}

class LichessPager implements SourcePager {
  done = false;
  private until: number | undefined; // ms, эксклюзивно; undefined = с самой свежей
  constructor(private nick: string) {}

  async next(signal?: AbortSignal): Promise<CoachGame[]> {
    if (this.done) return [];
    const base = `max=${PAGE}&rated=true&evals=true&opening=true&moves=true&pgnInJson=true`;
    const q = this.until ? `${base}&until=${this.until}` : base;
    const text = await getText(
      `https://lichess.org/api/games/user/${encodeURIComponent(this.nick)}?${q}`,
      { accept: 'application/x-ndjson' },
      signal,
    );
    const games = parseLichessNdjson(text);
    if (!games.length) {
      this.done = true;
      return [];
    }
    const oldest = Math.min(...games.map((g) => g.endTime || Infinity));
    if (Number.isFinite(oldest)) this.until = oldest * 1000; // следующая пачка — строго старше
    if (games.length < PAGE) this.done = true;
    return games;
  }
}

class ChessComPager implements SourcePager {
  done = false;
  private urls: string[] | null = null; // архивы месяцев, новые → старые
  private idx = 0;
  constructor(private nick: string) {}

  async next(signal?: AbortSignal): Promise<CoachGame[]> {
    if (this.done) return [];
    if (!this.urls) {
      const list = await getJson(`https://api.chess.com/pub/player/${encodeURIComponent(this.nick)}/games/archives`, signal);
      this.urls = ((list.archives as string[]) || []).slice().reverse();
    }
    const acc: CoachGame[] = [];
    while (this.idx < this.urls.length && acc.length < PAGE) {
      const archive = await getJson(this.urls[this.idx]!, signal);
      this.idx++;
      acc.push(...normalizeChessComArchiveGames([archive as never]));
    }
    if (this.idx >= this.urls.length) this.done = true;
    return acc;
  }
}

/** Пейджер списка партий: next() отдаёт следующую пачку (dedup по site:id, сорт по свежести). */
export class GamesPager {
  private sources: SourcePager[];
  private seen = new Set<string>();

  constructor(source: GameSource, nick: string) {
    const n = normalizeNick(nick);
    if (!isValidNick(n)) {
      this.sources = [];
      return;
    }
    if (source === 'lichess') this.sources = [new LichessPager(n)];
    else if (source === 'chesscom') this.sources = [new ChessComPager(n)];
    else this.sources = [new LichessPager(n), new ChessComPager(n)];
  }

  /** Больше данных нет (у всех источников). */
  get done(): boolean {
    return this.sources.length === 0 || this.sources.every((s) => s.done);
  }

  /**
   * Следующая пачка. Кидает ошибку (nick_not_found/сеть), только если ВСЕ
   * опрошенные источники упали — иначе отдаёт что удалось (dedup, сорт по свежести).
   */
  async next(signal?: AbortSignal): Promise<CoachGame[]> {
    const active = this.sources.filter((s) => !s.done);
    if (!active.length) return [];
    const settled = await Promise.allSettled(active.map((s) => s.next(signal)));
    const errors: unknown[] = [];
    const fresh: CoachGame[] = [];
    for (const r of settled) {
      if (r.status === 'rejected') {
        errors.push(r.reason);
        continue;
      }
      for (const g of r.value) {
        const key = `${g.site}:${g.id}`;
        if (g.id && this.seen.has(key)) continue;
        if (g.id) this.seen.add(key);
        fresh.push(g);
      }
    }
    if (errors.length === active.length) throw pickError(errors);
    fresh.sort((a, b) => (b.endTime || 0) - (a.endTime || 0));
    return fresh;
  }
}
